import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Classroom } from '../types';

interface ClassroomCache {
  [classroomId: string]: {
    data: Classroom | null;
    lastFetched: number;
    loading: boolean;
    error: string | null;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export const useClassroomCache = () => {
  console.log('[useClassroomCache] Hook called at:', new Date().toISOString());
  
  const [cache, setCache] = useState<ClassroomCache>({});
  const unsubscribeRefs = useRef<{ [key: string]: () => void }>({});

  // Clean up expired cache entries
  const cleanupExpiredCache = useCallback(() => {
    const now = Date.now();
    setCache(prevCache => {
      const newCache = { ...prevCache };
      Object.keys(newCache).forEach(key => {
        if (now - newCache[key].lastFetched > CACHE_DURATION) {
          delete newCache[key];
          // Unsubscribe from real-time updates
          if (unsubscribeRefs.current[key]) {
            unsubscribeRefs.current[key]();
            delete unsubscribeRefs.current[key];
          }
        }
      });
      return newCache;
    });
  }, []);

  // Clean up expired cache every minute
  useEffect(() => {
    const interval = setInterval(cleanupExpiredCache, 60 * 1000);
    return () => clearInterval(interval);
  }, [cleanupExpiredCache]);

  // Get classroom from cache or fetch if not cached
  const getClassroom = useCallback(async (classroomId: string): Promise<Classroom | null> => {
    const now = Date.now();
    const cached = cache[classroomId];

    // Return cached data if it's still valid
    if (cached && now - cached.lastFetched < CACHE_DURATION && cached.data) {
      return cached.data;
    }

    // Set loading state
    setCache(prev => ({
      ...prev,
      [classroomId]: {
        data: cached?.data || null,
        lastFetched: cached?.lastFetched || 0,
        loading: true,
        error: null
      }
    }));

    try {
      // Fetch from Firestore
      const classroomDoc = await getDoc(doc(db, 'classrooms', classroomId));
      
      if (classroomDoc.exists()) {
        const classroomData = { id: classroomDoc.id, ...classroomDoc.data() } as Classroom;
        
        // Update cache
        setCache(prev => ({
          ...prev,
          [classroomId]: {
            data: classroomData,
            lastFetched: now,
            loading: false,
            error: null
          }
        }));

        return classroomData;
      } else {
        // Classroom not found
        setCache(prev => ({
          ...prev,
          [classroomId]: {
            data: null,
            lastFetched: now,
            loading: false,
            error: 'Classroom not found'
          }
        }));
        return null;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch classroom';
      
      setCache(prev => ({
        ...prev,
        [classroomId]: {
          data: cached?.data || null,
          lastFetched: now,
          loading: false,
          error: errorMessage
        }
      }));
      
      throw error;
    }
  }, [cache]);

  // Subscribe to real-time updates for a classroom
  const subscribeToClassroom = useCallback((classroomId: string) => {
    console.log('[useClassroomCache] subscribeToClassroom called for:', classroomId, 'at:', new Date().toISOString());
    
    // Don't subscribe if already subscribed
    if (unsubscribeRefs.current[classroomId]) {
      console.log('[useClassroomCache] Already subscribed to:', classroomId);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'classrooms', classroomId),
      (doc) => {
        if (doc.exists()) {
          const classroomData = { id: doc.id, ...doc.data() } as Classroom;
          console.log('[useClassroomCache] Real-time update received for classroom:', classroomId, 'Messages count:', classroomData.messages?.length || 0);
          console.log('[useClassroomCache] Full classroom data:', classroomData);
          
          setCache(prev => {
            const newCache = {
              ...prev,
              [classroomId]: {
                data: classroomData,
                lastFetched: Date.now(),
                loading: false,
                error: null
              }
            };
            console.log('[useClassroomCache] Cache updated, new cache state:', newCache);
            return newCache;
          });
        } else {
          // Classroom was deleted
          setCache(prev => ({
            ...prev,
            [classroomId]: {
              data: null,
              lastFetched: Date.now(),
              loading: false,
              error: 'Classroom not found'
            }
          }));
        }
      },
      (error) => {
        console.error('[useClassroomCache] Error in real-time subscription for classroom:', classroomId, error);
        setCache(prev => ({
          ...prev,
          [classroomId]: {
            data: prev[classroomId]?.data || null,
            lastFetched: Date.now(),
            loading: false,
            error: error.message
          }
        }));
      }
    );

    unsubscribeRefs.current[classroomId] = unsubscribe;
  }, [cache]);

  // Unsubscribe from real-time updates
  const unsubscribeFromClassroom = useCallback((classroomId: string) => {
    console.log('[useClassroomCache] unsubscribeFromClassroom called for:', classroomId, 'at:', new Date().toISOString());
    
    if (unsubscribeRefs.current[classroomId]) {
      unsubscribeRefs.current[classroomId]();
      delete unsubscribeRefs.current[classroomId];
      console.log('[useClassroomCache] Unsubscribed from:', classroomId);
    } else {
      console.log('[useClassroomCache] No subscription found for:', classroomId);
    }
  }, []);

  // Update classroom in cache (for optimistic updates)
  const updateClassroomInCache = useCallback((classroomId: string, updates: Partial<Classroom>) => {
    setCache(prev => {
      const current = prev[classroomId];
      if (!current?.data) return prev;

      return {
        ...prev,
        [classroomId]: {
          ...current,
          data: { ...current.data, ...updates } as Classroom,
          lastFetched: Date.now()
        }
      };
    });
  }, []);

  // Remove classroom from cache
  const removeFromCache = useCallback((classroomId: string) => {
    setCache(prev => {
      const newCache = { ...prev };
      delete newCache[classroomId];
      return newCache;
        });
    
    // Unsubscribe from real-time updates
    unsubscribeFromClassroom(classroomId);
  }, [unsubscribeFromClassroom]);

  // Clear all cache
  const clearCache = useCallback(() => {
    // Unsubscribe from all real-time updates
    Object.keys(unsubscribeRefs.current).forEach(key => {
      unsubscribeRefs.current[key]();
    });
    unsubscribeRefs.current = {};
    
    setCache({});
  }, []);

  // Get cached classroom data
  const getCachedClassroom = useCallback((classroomId: string) => {
    return cache[classroomId] || null;
  }, [cache]);

  // Check if classroom is cached
  const isCached = useCallback((classroomId: string) => {
    const cached = cache[classroomId];
    return cached && Date.now() - cached.lastFetched < CACHE_DURATION;
  }, [cache]);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    const total = Object.keys(cache).length;
    const valid = Object.values(cache).filter(
      entry => now - entry.lastFetched < CACHE_DURATION
    ).length;
    const expired = total - valid;

    return { total, valid, expired };
  }, [cache]);

  return {
    getClassroom,
    subscribeToClassroom,
    unsubscribeFromClassroom,
    updateClassroomInCache,
    removeFromCache,
    clearCache,
    getCachedClassroom,
    isCached,
    getCacheStats,
    cache
  };
};
